import React from 'react';
import { Divider, Thumbnail } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { SectionWidthEditor } from './SectionWidthEditor';
import { SectionHeightEditor } from './SectionHeightEditor';
import { SectionBackgroundEditor } from './SectionBackgroundColorEditor';
import { SectionLayoutEditor } from './SectionLayoutEditor';
import { SectionAlignmentEditor } from './SectionAlignmentEditor';
import { SectionPaddingEditor } from './SectionPaddingEditor';
import { SectionWidgetSpacingEditor } from './SectionWidgetSpacingEditor';
import { SectionColumnGapEditor } from './SectionColumnGapEditor';
import { SettingsAccordion } from '../SettingsAccordion';
import { isDevelopment } from '../../../../constants/app';
import CustomCssStyleEditor from '../CustomCSSEditor';
import { useActiveSection } from '../../../../queries/guides';
import { useSectionMutations } from '../../../../mutations/sections';
import { useDebouncedCallback } from 'use-debounce';
import { INSPECTOR_DEBOUNCE_DELAY } from '../constants';
import {
  IconAspectRatioOutline,
  IconBackgroundFill,
  IconHeightS,
  IconLayout,
  IconPadding,
} from '@bynder/icons';
import { updateStyles } from '../helpers';

export const SectionSettings: React.FC = () => {
  const { translate } = useTranslate();
  const { activeSection } = useActiveSection();
  const { editSectionStyle } = useSectionMutations();

  const onSetStyle = (style: string) => {
    editSectionStyle.mutate({
      sectionId: activeSection.id,
      style: updateStyles(activeSection.style.stylesheet, style),
    });
  };
  const debouncedSetStyle = useDebouncedCallback(
    onSetStyle,
    INSPECTOR_DEBOUNCE_DELAY,
  );

  const handleStyleChange = (value: string) => {
    debouncedSetStyle(value);
  };

  if (!activeSection) return null;
  return (
    <>
      <SettingsAccordion
        thumbnail={<Thumbnail icon={<IconBackgroundFill />} />}
        title={translate('SECTION_INSPECTOR.BACKGROUND_COLOR_TITLE')}
      >
        <SectionBackgroundEditor />
      </SettingsAccordion>
      <SettingsAccordion
        thumbnail={<Thumbnail icon={<IconLayout />} />}
        title={translate('SECTION_INSPECTOR.LAYOUT_TITLE')}
      >
        <SectionLayoutEditor />
        <SectionColumnGapEditor />
      </SettingsAccordion>
      <SettingsAccordion
        thumbnail={<Thumbnail icon={<IconAspectRatioOutline />} />}
        title={translate('SECTION_INSPECTOR.SECTION_SIZE_TITLE')}
      >
        <>
          <SectionWidthEditor />
          <SectionHeightEditor
            handleStyleChange={handleStyleChange}
            activeSectionId={activeSection.id}
            activeSectionHeight={activeSection.appearance?.height}
            activeSectionStyleSheet={activeSection.style.stylesheet}
          />
        </>
      </SettingsAccordion>
      <SettingsAccordion
        thumbnail={<Thumbnail icon={<IconPadding />} />}
        title={translate('SECTION_INSPECTOR.SECTION_PADDING')}
      >
        <SectionPaddingEditor />
      </SettingsAccordion>
      <SettingsAccordion
        thumbnail={<Thumbnail icon={<IconHeightS />} />}
        subtext={translate('SECTION_INSPECTOR.SECTION_WIDGET_SUBTITLE')}
        title={translate('SECTION_INSPECTOR.SECTION_WIDGET_TITLE')}
      >
        <SectionAlignmentEditor />
        <Divider />
        <SectionWidgetSpacingEditor />
      </SettingsAccordion>
      {isDevelopment && (
        <CustomCssStyleEditor
          label="Section CSS"
          placeholder="Enter your custom CSS here"
          style={activeSection?.style?.stylesheet}
        />
      )}
    </>
  );
};
